import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { DashboardResolverService } from 'app/modules/tma/dashboard-shell/services/dashboard-resolver.service';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { SmartRedirectGuard } from 'app/core/auth/guards/smart-redirect.guard';
import { AnalyticsGuard } from 'app/core/auth/guards/analytics.guard';
import { DevelopmentGuard } from 'app/core/auth/guards/development.guard';
import { DemoGuard } from 'app/core/auth/guards/demo.guard';
import { CmsDataGuard } from 'app/core/auth/guards/cms-data.guard';
import { SystemToolsGuard } from 'app/core/auth/guards/system-tools.guard';
import { LayoutComponent } from 'app/layout/layout.component';

/**
 * ============================================================================
 * ROUTE CONFIGURATION - ROLE-BASED ACCESS
 * ============================================================================
 *
 * Routes are protected by guards based on user roles.
 * See roles.model.ts for the complete role → route mapping.
 *
 * ROUTE GUARDS:
 * - AuthGuard: Requires authentication (any logged-in user)
 * - AnalyticsGuard: OrganizationAdmin, AnalyticsAdmin, Development, Demo
 * - DevelopmentGuard: Development role only
 * - DemoGuard: Demo role only
 * - CmsDataGuard: SuperAdmin, SystemAdmin, OR Demo
 * - SystemToolsGuard: Development OR Demo
 * - AdminGuard: SuperAdmin, SystemAdmin only (in admin.routes.ts)
 *
 * TO ADD A NEW ROUTE:
 * 1. Add the route definition below
 * 2. Add the appropriate guard(s)
 * 3. Add the route path to ROUTE_CONFIG in roles.model.ts
 * ============================================================================
 */

export const appRoutes: Route[] = [

    // Smart redirect: admins go to /admin/select-context, others to /dashboardv2
    {
        path: '',
        pathMatch: 'full',
        canActivate: [AuthGuard, SmartRedirectGuard],
        // Component required for guards to run, but we always redirect
        loadComponent: () => import('app/modules/tma/landing-dashboard/landing-dashboard.component')
            .then(m => m.LandingDashboardComponent)
    },

    // Redirect after sign-in - use smart redirect
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        canActivate: [AuthGuard, SmartRedirectGuard],
        loadComponent: () => import('app/modules/tma/landing-dashboard/landing-dashboard.component')
            .then(m => m.LandingDashboardComponent)
    },

    // Auth routes for guests (only sign-in available for B2B)
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
        ]
    },

    // Main application routes (all require authentication)
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            // =================================================================
            // UNAUTHORIZED - Users with no roles
            // =================================================================
            {
                path: 'unauthorized',
                loadComponent: () => import('app/modules/auth/unauthorized')
                    .then(m => m.UnauthorizedComponent)
            },

            // =================================================================
            // ANALYTICS ROUTES (OrganizationAdmin, AnalyticsAdmin, Development, Demo)
            // =================================================================

            // Landing Dashboard
            {
                path: 'dashboard',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/landing-dashboard/landing-dashboard.component')
                    .then(m => m.LandingDashboardComponent)
            },

            // Medent Comparison Reports — user-side surface for Kind='report' templates
            // (also reachable at /admin/medent-reports for SuperAdmin/SystemAdmin).
            {
                path: 'medent-reports',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/admin/medent-reports/medent-reports.component')
                    .then(m => m.MedentReportsComponent)
            },

            // Dashboard V2 - Triad Dashboard
            {
                path: 'dashboardv2',
                canActivate: [AnalyticsGuard],
                loadChildren: () => import('app/modules/tma/dashboardv2/dashboardv2.routes')
            },

            // Practice Dashboard V0
            {
                path: 'practice-dashboard',
                canActivate: [AnalyticsGuard],
                loadChildren: () => import('app/modules/tma/practice-dashboard/practice-dashboard.routes')
            },

            // Practice Dashboard V1
            {
                path: 'practice-dashboard-v1',
                canActivate: [AnalyticsGuard],
                loadChildren: () => import('app/modules/tma/practice-dashboard-v1/practice-dashboard-v1.routes')
            },

            // Sequencing Recovery
            {
                path: 'sequencing-recovery',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/sequencing-recovery/sequencing-recovery.component')
                    .then(m => m.SequencingRecoveryComponent)
            },

            // CPT Dashboard routes
            {
                path: 'cpt',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/cpt-dashboard/cpt-list/cpt-list.component')
                    .then(m => m.CptListComponent)
            },
            // Legacy CPT detail → redirect to new TMA Meta Dashboard
            {
                path: 'cpt/:cptCode',
                redirectTo: '/tmamdv1/cpt/:cptCode',
                pathMatch: 'full'
            },

            // Payer Dashboard routes
            {
                path: 'payer',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/payer-dashboard/payer-list/payer-list.component')
                    .then(m => m.PayerListComponent)
            },
            {
                path: 'payer/:payorId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/payer-dashboard/payer-detail/payer-detail.component')
                    .then(m => m.PayerDetailComponent)
            },

            // Doctor Dashboard routes
            {
                path: 'doctor',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/doctor-dashboard/doctor-list/doctor-list.component')
                    .then(m => m.DoctorListComponent)
            },
            {
                path: 'doctor/:providerId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/doctor-dashboard/doctor-detail/doctor-detail.component')
                    .then(m => m.DoctorDetailComponent)
            },

            // TMA Icon Gallery
            {
                path: 'dashboard-v3/tma-icons',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-v3/ui-test/icon-gallery.component')
                    .then(m => m.IconGalleryComponent)
            },

            // UI Test Dashboard — renders the canonical UI Test MetaDashboard via the shared shell.
            // No wrapper component. The resolver lazily imports the meta config; route data
            // (resolved + static) is auto-bound to the component's @Input() meta thanks to
            // withComponentInputBinding() in app.config.ts.
            {
                path: 'dashboard-v3/ui-test',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/dashboard-v3/ui-test/ui-test.meta')
                        .then(m => m.UI_TEST_META_DASHBOARD)
                }
            },

            // Dashboard V3 - Manifest-driven dashboards (standalone)
            {
                path: 'dashboard-v3/:dashboardId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-v3/dashboard-v3.component')
                    .then(m => m.DashboardV3Component)
            },

            // Meta-Dashboard - Tabbed container with dashboard-v3 tabs
            {
                path: 'dashboard-v3/meta/:metaDashboardId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-v2/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent)
            },

            // Dashboard V2 Meta (legacy route)
            {
                path: 'dashboard-v2/meta/:metaDashboardId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-v2/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent)
            },

            // Dashboard V2 - Generalized config-driven dashboards
            {
                path: 'dashboard-v2/:dashboardId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-v2/dashboard-v2.component')
                    .then(m => m.DashboardV2Component)
            },

            // Navigation Tips (coming soon)
            {
                path: 'navigation-tips',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/navigation-tips/navigation-tips.component')
                    .then(m => m.NavigationTipsComponent)
            },

            // Data Extraction V2 (all analytics users)
            {
                path: 'data-extraction',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/admin/silver-extraction-v2/silver-extraction-v2.component')
                    .then(m => m.SilverExtractionV2Component)
            },

            // ── AI widget-builder sandbox ───────────────────────────────────────
            // Single-slot scratch space: opens the most-recent AI-drafted widget
            // inside a synthetic MetaDashboard wrapper so the real widget renderer
            // can display actual data. Overwritten each time the user clicks
            // "Add to dashboard" in the chat panel.
            {
                path: 'dashboards/ai-draft/sandbox',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-v3/ai/ai-draft-sandbox.component')
                    .then(m => m.AiDraftSandboxComponent)
            },

            // ── Practice-wide dashboards (Project Constellation Phase 2+) ───────
            {
                path: 'dashboards/key-finance',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/key-finance/key-finance.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('key-finance', m.KEY_FINANCE_META_DASHBOARD))
                }
            },
            {
                path: 'dashboards/key-ops',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/key-ops/key-ops.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('key-ops', m.KEY_OPS_META_DASHBOARD))
                }
            },

            {
                path: 'dashboards/key-referrals',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/key-referrals/key-referrals.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('key-referrals', m.KEY_REFERRALS_META_DASHBOARD))
                }
            },
            {
                path: 'dashboards/key-regulation',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/key-regulation/key-regulation.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('key-regulation', m.KEY_REGULATION_META_DASHBOARD))
                }
            },
            {
                path: 'dashboards/other-finance',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/other-finance/other-finance.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('other-finance', m.OTHER_FINANCE_META_DASHBOARD))
                }
            },
            {
                path: 'dashboards/other-ops',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/other-ops/other-ops.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('other-ops', m.OTHER_OPS_META_DASHBOARD))
                }
            },

            {
                path: 'dashboards/new-patient',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: () => import('app/modules/tma/main-dashboards/new-patient/new-patient.meta')
                        .then(m => inject(DashboardResolverService).resolveMeta('new-patient', m.NEW_PATIENT_META_DASHBOARD))
                }
            },

            // ── Practice Overview Drill-Downs (Cosmos-driven) ──────────────
            // SHIPPED 2026-05-04: replaced 17 hand-written `dashboards/po-*`
            // routes with one generic Cosmos-resolving route. The resolver looks
            // up the metaId in the Cosmos cache (METADASHBOARDV4) first; falls
            // back to the TS registry (`findMetaInRegistry`) when offline; falls
            // back to a synthetic "Not Found" placeholder if both miss. New AR-
            // split metas (po-{gross,net}-*-detail) work without a code deploy.
            //
            // Routes for `dashboards/{key-finance, key-ops, key-referrals,
            // key-regulation, other-finance, other-ops, new-patient}` are
            // registered ABOVE this and match first (Angular evaluates in order).
            {
                path: 'dashboards/:metaId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/dashboard-shell/meta-dashboard.component')
                    .then(m => m.MetaDashboardComponent),
                resolve: {
                    meta: (route: ActivatedRouteSnapshot) =>
                        inject(DashboardResolverService).resolveMetaById(route.paramMap.get('metaId')!)
                }
            },

            // TMA Meta Dashboard v1 — unified meta-dashboard renderer
            // Hosts: ui-test, encounter, service-line, cpt (and future entity dashboards)
            {
                path: 'tmamdv1',
                canActivate: [AnalyticsGuard],
                loadChildren: () => import('app/modules/tma/entity-dashboards/entity-dashboards.routes')
                    .then(m => m.ENTITY_DASHBOARDS_ROUTES)
            },

            // Entity Spotlights — picker landing pages. /spotlight/<entity> →
            // EntityPicker → row click → /tmamdv1/<entity>/:entityId (spotlight meta).
            {
                path: 'spotlight',
                canActivate: [AnalyticsGuard],
                loadChildren: () => import('app/modules/tma/entity-dashboards/entity-dashboards.routes')
                    .then(m => m.ENTITY_SPOTLIGHTS_ROUTES)
            },

            // User Dashboard (working links only)
            {
                path: 'user-dashboard',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/user-dashboard/user-dashboard.component')
                    .then(m => m.UserDashboardComponent)
            },

            // Coming Soon placeholder
            {
                path: 'coming-soon',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/main-dashboard/main-dashboard.component')
                    .then(m => m.MainDashboardComponent)
            },

            // Main Dashboard (navigation hub)
            {
                path: 'main-dashboard',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/main-dashboard/main-dashboard.component')
                    .then(m => m.MainDashboardComponent)
            },

            // Practice Overview — headline 4-tab MetaDashboard + hub nav strip
            {
                path: 'practice-overview',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/practice-overview-home/practice-overview-home.component')
                    .then(m => m.PracticeOverviewHomeComponent)
            },

            // Practice Trends — YoY comparison + segmented trend charts; "Go" builds gold
            {
                path: 'practice-trends',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/practice-trends/practice-trends.component')
                    .then(m => m.PracticeTrendsComponent)
            },

            // Appointment Management Summary Dashboard — per-queue KPIs by appointment date
            {
                path: 'appointments/dashboard',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/worklist-hub/appointment-dashboard.component')
                    .then(m => m.AppointmentDashboardComponent)
            },

            // Appointment Worklists — morning queues from the nightly appointment ingest
            {
                path: 'appointments/worklists',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/worklist-hub/worklist-hub.component')
                    .then(m => m.WorklistHubComponent)
            },

            // Rebooking Reports — last appt fell through (C/N or P) and nothing upcoming
            {
                path: 'appointments/reports',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/worklist-hub/appointment-reports.component')
                    .then(m => m.AppointmentReportsComponent)
            },

            // Goals — read-only overview (user-facing); authoring lives under /admin/goals
            {
                path: 'goals',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/admin/goals/goals-overview.component')
                    .then(m => m.GoalsOverviewComponent)
            },

            // Data Extraction V3 (simplified, table-first)
            {
                path: 'data-extraction-v3',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/admin/data-extraction-v3/data-extraction-v3.component')
                    .then(m => m.DataExtractionV3Component)
            },

            // TMA Pivot — Excel-style pivot grid w/ semantic field rail
            {
                path: 'tma-pivot',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/admin/tma-pivot/tma-pivot.component')
                    .then(m => m.TmaPivotComponent)
            },

            // TMA Accounting Pivot — same component, locked to service lines + posting-date scope/grain
            {
                path: 'accounting/pivot',
                canActivate: [AnalyticsGuard],
                data: { accountingMode: true },
                loadComponent: () => import('app/modules/admin/tma-pivot/tma-pivot.component')
                    .then(m => m.TmaPivotComponent)
            },

            // Accounting — entry-date GL ledger + unapplied-cash deposits
            {
                path: 'accounting/explorer',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/accounting/accounting-explorer.component')
                    .then(m => m.AccountingExplorerComponent)
            },
            {
                path: 'accounting/deposits',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/accounting/accounting-deposits.component')
                    .then(m => m.AccountingDepositsComponent)
            },
            // Visit Accounting — GL statement (finder + deep-link by encounterId)
            {
                path: 'accounting/visit',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/accounting/accounting-visit.component')
                    .then(m => m.AccountingVisitComponent)
            },
            {
                path: 'accounting/visit/:encounterId',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/accounting/accounting-visit.component')
                    .then(m => m.AccountingVisitComponent)
            },
            // Cost & Profitability — one margin engine, three default grains
            {
                path: 'accounting/job-costing',
                canActivate: [AnalyticsGuard],
                data: { defaultGrain: 'Visit', title: 'Job Costing', icon: 'tma:medicare-rates' },
                loadComponent: () => import('app/modules/tma/accounting/accounting-margin.component')
                    .then(m => m.AccountingMarginComponent)
            },
            {
                path: 'accounting/margin',
                canActivate: [AnalyticsGuard],
                data: { defaultGrain: 'Facility', title: 'Contribution Margin', icon: 'tma:revenue-enhancement' },
                loadComponent: () => import('app/modules/tma/accounting/accounting-margin.component')
                    .then(m => m.AccountingMarginComponent)
            },
            {
                path: 'accounting/productivity',
                canActivate: [AnalyticsGuard],
                data: { defaultGrain: 'Attending', title: 'Provider Productivity', icon: 'tma:dm-rendering-physician' },
                loadComponent: () => import('app/modules/tma/accounting/accounting-margin.component')
                    .then(m => m.AccountingMarginComponent)
            },
            // Cost Model editor (feeds the margin engine)
            {
                path: 'accounting/settings/cost-model',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/accounting/accounting-cost-model.component')
                    .then(m => m.AccountingCostModelComponent)
            },

            // TM AI — Build with me (single AI surface; Phase 1 shell)
            {
                path: 'tm-ai/build-with-me',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/ai/build-with-me/build-with-me.component')
                    .then(m => m.BuildWithMeComponent)
            },

            // TMA Insights V2 — staged appointments-focused chat revamp (admin nav for now)
            {
                path: 'tm-ai/insights-v2',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/ai/insights-v2/insights-v2.component')
                    .then(m => m.InsightsV2Component)
            },

            // TMA-Insights — Audit Review (admin triage of the ai-audit log; backend gates to SuperAdmin/SystemAdmin)
            {
                path: 'tm-ai/audit-review',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/ai/audit-review/tm-ai-audit.component')
                    .then(m => m.TmAiAuditComponent)
            },

            // Service Line View (line-level detail with accounting + sequencing)
            {
                path: 'service-line-view',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/service-line-view/service-line-view.component')
                    .then(m => m.ServiceLineViewComponent)
            },

            // Visit View — canonical route (renamed 2026-05-13 for consistency)
            {
                path: 'visit-view',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/encounter-view/encounter-view.component')
                    .then(m => m.EncounterViewComponent)
            },
            // Legacy alias — many dashboards + AI tooling still emit /encounter-view
            // links. Same component, both paths work indefinitely.
            {
                path: 'encounter-view',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/encounter-view/encounter-view.component')
                    .then(m => m.EncounterViewComponent)
            },

            // Fee Schedule Intelligence Dashboard
            {
                path: 'fee-schedule-intelligence',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/fee-schedule-intelligence/fee-schedule-intelligence.component')
                    .then(m => m.FeeScheduleIntelligenceComponent)
            },

            // Fee Schedule Extraction (data-extraction-v3 style)
            {
                path: 'fee-schedule-extraction',
                canActivate: [AnalyticsGuard],
                loadComponent: () => import('app/modules/tma/fee-schedule-extraction/fee-schedule-extraction.component')
                    .then(m => m.FeeScheduleExtractionComponent)
            },

            // =================================================================
            // DEMO ROUTES (Demo role only)
            // =================================================================
            {
                path: 'demo',
                canActivate: [DemoGuard],
                loadChildren: () => import('app/modules/tma/demo/demo.routes')
            },

            // =================================================================
            // CMS MEDICAL DATA ROUTES (Admin + Demo)
            // =================================================================
            {
                path: 'cms',
                canActivate: [CmsDataGuard],
                canActivateChild: [CmsDataGuard],
                children: [
                    {
                        path: 'data-loader',
                        loadComponent: () => import('app/modules/admin/data-loader/data-loader.component')
                            .then(m => m.DataLoaderComponent)
                    },
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'data-loader'
                    }
                ]
            },

            // =================================================================
            // SIMULATED DEMO TOOLS ROUTES (Development + Demo)
            // Direct component loading without parent wrapper
            // =================================================================
            {
                path: 'system',
                canActivate: [SystemToolsGuard],
                canActivateChild: [SystemToolsGuard],
                children: [
                    {
                        path: 'create-demo-data',
                        loadComponent: () => import('app/modules/tma/admin/tabs/simulated-data/simulated-data.component')
                            .then(m => m.SimulatedDataComponent)
                    },
                    {
                        path: 'testing-tools',
                        loadComponent: () => import('app/modules/tma/admin/tabs/testing-tools/testing-tools.component')
                            .then(m => m.TestingToolsComponent)
                    },
                    {
                        path: 'system-testing',
                        loadComponent: () => import('app/modules/tma/admin/tabs/system-testing/system-testing.component')
                            .then(m => m.SystemTestingComponent)
                    },
                    {
                        path: 'activity-tracking',
                        loadComponent: () => import('app/modules/tma/admin/tabs/activity-tracking/activity-tracking.component')
                            .then(m => m.ActivityTrackingComponent)
                    },
                    {
                        path: 'data-loader',
                        loadComponent: () => import('app/modules/tma/admin/tabs/data-loader/data-loader.component')
                            .then(m => m.DataLoaderComponent)
                    },
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'create-demo-data'
                    }
                ]
            },

            // =================================================================
            // DEVELOPMENT ROUTES (Development role only)
            // =================================================================
            {
                path: 'dev',
                canActivate: [DevelopmentGuard],
                canActivateChild: [DevelopmentGuard],
                children: [
                    {
                        path: 'upload-fee-schedule-dev',
                        loadChildren: () => import('app/modules/tma/billing-analytics/fee-schedule-upload/fee-schedule-upload.routes')
                    },
                    {
                        path: 'analytics-dashboard',
                        loadChildren: () => import('app/modules/tma/analytics-v3/analytics-v3.routes')
                    },
                    {
                        path: 'upload',
                        loadChildren: () => import('app/modules/tma/upload-dashboard/upload-dashboard.routes')
                    },
                    {
                        path: 'dashboard',
                        loadChildren: () => import('app/modules/tma/dashboard/dashboard.routes')
                    },
                    {
                        path: 'onboarding',
                        loadChildren: () => import('app/modules/tma/onboarding/onboarding.routes')
                    },
                    {
                        path: 'data-linking',
                        loadChildren: () => import('app/modules/tma/data-linking/data-linking.module').then(m => m.DataLinkingModule)
                    },
                    {
                        path: 'patient-encounter-upload',
                        loadChildren: () => import('app/modules/tma/billing-analytics/patient-encounter-upload/patient-encounter-upload.routes')
                    },
                    {
                        path: 'provider-plan-upload',
                        loadChildren: () => import('app/modules/tma/billing-analytics/provider-plan-upload/provider-plan-upload.routes')
                    },
                    {
                        path: 'admin/data-loader',
                        loadComponent: () => import('app/modules/admin/data-loader/data-loader.component')
                            .then(m => m.DataLoaderComponent)
                    },
                    {
                        path: 'admin/encounter-matching',
                        loadComponent: () => import('app/modules/admin/encounter-matching/encounter-matching.component')
                            .then(m => m.EncounterMatchingComponent)
                    },
                    {
                        path: 'sequencing-recovery',
                        loadComponent: () => import('app/modules/tma/sequencing-recovery/sequencing-recovery.component')
                            .then(m => m.SequencingRecoveryComponent)
                    },
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'main-dashboard'
                    }
                ]
            },

            // =================================================================
            // ADMIN ROUTES (SuperAdmin, SystemAdmin only)
            // Guard applied in admin.routes.ts
            // =================================================================
            {
                path: 'admin',
                loadChildren: () => import('app/modules/admin/admin.routes')
            },

            // Legacy admin-settings route - redirects to /system
            {
                path: 'admin-settings',
                redirectTo: 'system',
                pathMatch: 'prefix'
            },

            // Example module (development only)
            {
                path: 'example',
                canActivate: [DevelopmentGuard],
                loadChildren: () => import('app/modules/admin/example/example.routes')
            },
        ]
    }
];